'use client';

import { Fragment, useEffect, useRef, useState } from 'react';
import type { NavBlockProps } from '../types';
import { theme } from '../theme';

/**
 * Pure-ish site header / menu. Logo + links + optional CTA, with a no-JS mobile
 * hamburger (<details>/<summary>).
 *
 * Two modes:
 *  - default (overlayHero false): a solid sticky bar (brand background).
 *  - overlayHero true: the bar overlays a full-bleed hero — transparent (light
 *    text over a subtle scrim) at the very top, turning solid (brand background)
 *    once the visitor scrolls. A negative bottom margin pulls the next block up
 *    underneath the bar so the hero can run edge-to-edge to the top of the page.
 *
 * The scroll-driven flip is the only reason this is a client component; everything
 * else (including the hamburger) is plain markup. The transparent state needs a
 * dark hero behind it to look right — so it's opt-in, and is best previewed on the
 * live site / mobile device frame (the desktop click-to-edit canvas renders blocks
 * in isolation, so it can't show the over-hero overlay).
 */
export function NavBlock({ logoText, logoUrl, logoOverlayUrl, links, ctaLabel, ctaHref, overlayHero }: NavBlockProps) {
  const ref = useRef<HTMLElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!overlayHero) return;
    // Detect "scrolled away from the top" with an IntersectionObserver on a 0-height
    // sentinel at the very top of the page. This is scroll-source-agnostic — it works
    // whether the page scrolls the window (live site) OR an iframe's own viewport
    // (the builder's preview frame) — using the SENTINEL'S OWN window so the root is
    // the right viewport. Falls back to a scroll listener if IO is unavailable.
    const sentinel = sentinelRef.current;
    const win = (sentinel ?? ref.current)?.ownerDocument?.defaultView ?? window;
    if (sentinel && typeof win.IntersectionObserver === 'function') {
      const io = new win.IntersectionObserver(
        ([entry]) => setScrolled(!entry.isIntersecting),
        { root: null, threshold: 0 },
      );
      io.observe(sentinel);
      return () => io.disconnect();
    }
    const onScroll = () => setScrolled(win.scrollY > 8);
    onScroll();
    win.addEventListener('scroll', onScroll, { passive: true });
    return () => win.removeEventListener('scroll', onScroll);
  }, [overlayHero]);

  const transparent = !!overlayHero && !scrolled;
  const solid = !overlayHero || scrolled;
  // Switch the logo WITH the bar (mirrors the live <Navigation>): the dark/reversed logo over the
  // dark hero, then the light-background (primary) logo once the bar turns solid.
  const displayLogoUrl = transparent ? (logoOverlayUrl || logoUrl) : (logoUrl || logoOverlayUrl);

  const headerStyle = transparent
    ? {
        // Stronger top scrim + a text shadow keep the light links legible over ANY hero —
        // including light/teal/photographic heroes, not just dark ones.
        backgroundImage: 'linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0.05))',
        color: '#ffffff',
        textShadow: '0 1px 4px rgba(0,0,0,0.45)',
      }
    : { backgroundColor: theme.background, color: theme.text };

  const headerClass = [
    'z-30 flex items-center justify-between px-6 transition-colors duration-300',
    // Overlay mode: a FIXED-height bar pinned to the viewport so it reliably stays
    // on screen over the hero (sticky is fragile inside scroll containers / frames).
    // Default mode: the classic in-flow solid sticky bar.
    overlayHero ? 'fixed inset-x-0 top-0 h-20' : 'sticky top-0 py-4',
    solid ? 'border-b border-black/10 backdrop-blur' : '',
  ].join(' ');

  const cta =
    ctaLabel && ctaHref ? (
      <a
        href={ctaHref}
        className="rounded-md px-4 py-2 text-sm font-medium text-white hover:opacity-90"
        style={{ backgroundColor: theme.primary }}
      >
        {ctaLabel}
      </a>
    ) : null;

  return (
    <>
      {/* Top-of-page sentinel: the IntersectionObserver flips the bar solid once
          this scrolls out of view. Only needed in overlay mode (0 height, in flow). */}
      {overlayHero ? <div ref={sentinelRef} aria-hidden className="h-px w-full" /> : null}
      <header ref={ref} className={headerClass} style={headerStyle}>
        {/* Brand logo image when set (matches the live header), else the text logo. */}
        <a href="/" className="flex shrink-0 items-center" aria-label="Home">
          {displayLogoUrl ? (
            <img src={displayLogoUrl} alt={logoText || 'Home'} className="h-10 w-auto object-contain" />
          ) : (
            <span className="text-xl font-semibold tracking-wide" style={{ fontFamily: theme.headingFont }}>
              {logoText}
            </span>
          )}
        </a>

      {/* Desktop: inline links — inherit the header color so they flip on scroll. A link
          with children renders as a hover dropdown (one level deep) — mirrors the live header. */}
      <nav className="hidden items-center gap-6 md:flex">
        {links.map((l, i) =>
          l.children && l.children.length > 0 ? (
            <div key={`${i}-${l.label}`} className="group relative">
              <a href={l.href} className="flex items-center gap-1 text-sm font-medium hover:opacity-70" style={{ color: 'inherit' }}>
                {l.label}
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </a>
              {/* Always-solid panel for legibility (matches the mobile menu + live header). */}
              {/* NO OPACITY FADE. This is why the dropdown text looked blurry, reported twice.
                  An element with 0 < opacity < 1 is composited into its own layer, and a
                  composited layer cannot use subpixel (LCD) antialiasing — the browser falls back
                  to greyscale AA, which renders text visibly thinner and softer than the sharp
                  nav labels beside it. Chromium usually restores subpixel AA once the transition
                  settles at exactly 1, but an element that keeps animating opacity can stay
                  promoted, and stay soft.
                  `visibility` alone creates no layer at all, so the text rasterises exactly like
                  the rest of the header. The 150ms fade is not worth illegible menu items. */}
              <div className="invisible absolute left-0 top-full z-40 pt-2 group-hover:visible">
                {/* textShadow: none — the overlay header sets a text shadow on the whole <header>
                    for legibility over the hero, and text-shadow INHERITS. Without this reset the
                    solid panel renders dark text with the hero's dark shadow (field report,
                    twice: "the drop down menu text seems to have a drop shadow or blur"). */}
                <div className="flex min-w-[12rem] flex-col rounded-md border border-black/10 py-1 shadow-lg" style={{ backgroundColor: theme.background, color: theme.text, textShadow: 'none' }}>
                  {l.children.map((c) => (
                    <a key={c.href} href={c.href} className="px-4 py-2 text-sm font-medium hover:opacity-70" style={{ color: theme.text }}>
                      {c.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <a key={`${i}-${l.href}`} href={l.href} className="text-sm font-medium hover:opacity-70" style={{ color: 'inherit' }}>
              {l.label}
            </a>
          ),
        )}
      </nav>

      {/* Desktop: CTA (keeps the original three-up layout) */}
      <div className="hidden md:block">{cta ?? <span aria-hidden />}</div>

      {/* Mobile: no-JS hamburger menu (dropdown panel is always solid for legibility) */}
      <details className="relative md:hidden [&_summary::-webkit-details-marker]:hidden">
        <summary
          className="flex cursor-pointer list-none items-center justify-center rounded-md p-2"
          aria-label="Open menu"
          style={{ color: 'inherit' }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </summary>
        <div
          className="absolute right-0 top-full z-30 mt-2 flex w-56 flex-col gap-1 rounded-md border border-black/10 p-3 shadow-lg"
          // textShadow: none — same reset as the desktop dropdown: the overlay header's
          // hero-legibility text shadow inherits into this solid panel otherwise.
          style={{ backgroundColor: theme.background, color: theme.text, textShadow: 'none' }}
        >
          {links.map((l, i) => (
            <Fragment key={`${i}-${l.href}`}>
              <a
                href={l.href}
                className="rounded px-2 py-1.5 text-sm font-medium hover:opacity-70"
                style={{ color: theme.text }}
              >
                {l.label}
              </a>
              {l.children?.map((c) => (
                <a
                  key={c.href}
                  href={c.href}
                  className="rounded px-2 py-1.5 pl-5 text-sm hover:opacity-70"
                  style={{ color: theme.text }}
                >
                  {c.label}
                </a>
              ))}
            </Fragment>
          ))}
          {cta ? <div className="pt-1">{cta}</div> : null}
        </div>
      </details>
      </header>
    </>
  );
}
